// Browser equivalent of android/.../data/DevicePreferences.kt (DataStore on Android,
// localStorage here). Persists the player's identity + last-known content so a
// reopened tab boots into "playing" without waiting for the server.
//
// `wt_device_uid` is intentionally kept across clear()s so the player presents
// the *same* hardware identity to /devices/register after a token wipe — the
// backend keys on device_uid and hands back the existing Screen's token
// instead of inserting a duplicate (which would crash on the UNIQUE
// constraint). See backend/app/api/routes/devices.py:register_device.

import type { ContentResponse } from './deviceApi'

const KEY_TOKEN          = 'wt_device_token'
const KEY_SCREEN_ID      = 'wt_screen_id'
const KEY_DEVICE_UID     = 'wt_device_uid'
const KEY_CACHED         = 'wt_cached_content'
// Epoch-ms of the last successful /content fetch. Used by the cache-TTL
// eviction logic in usePlayerState (after N hours of no successful fetch
// we drop cached content and show the offline screen).
const KEY_LAST_FETCHED   = 'wt_last_fetched_at'
// ── License countdown ────────────────────────────────────────────────────
// disconnect_at is the ISO timestamp the player echoes back in any support
// overlay; countdown_hours is the live remaining hours value the monotonic
// loop decrements. Both seeded by every successful server response.
const KEY_DISCONNECT_AT       = 'wt_disconnect_at'
const KEY_COUNTDOWN_HOURS     = 'wt_countdown_hours'
const KEY_COUNTDOWN_LAST_WALL = 'wt_countdown_last_wall'
const KEY_REGISTER_RETRY_AT   = 'wt_register_retry_at'

export function loadToken(): string | null {
  return localStorage.getItem(KEY_TOKEN)
}

export function loadScreenId(): number | null {
  const raw = localStorage.getItem(KEY_SCREEN_ID)
  if (!raw) return null
  const n = parseInt(raw, 10)
  return Number.isFinite(n) ? n : null
}

/** Lazily-generated stable UUID. Survives clear() so re-registers can reuse it. */
export function loadDeviceUid(): string {
  let uid = localStorage.getItem(KEY_DEVICE_UID)
  if (!uid) {
    uid = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
      ? crypto.randomUUID()
      : `wt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    localStorage.setItem(KEY_DEVICE_UID, uid)
  }
  return uid
}

export function publicDeviceId(): string {
  return loadDeviceUid().replace(/[^a-zA-Z0-9]/g, '').slice(0, 12).toUpperCase()
}

export function loadRegisterRetryAt(): number | null {
  const raw = localStorage.getItem(KEY_REGISTER_RETRY_AT)
  if (!raw) return null
  const n = parseInt(raw, 10)
  return Number.isFinite(n) ? n : null
}

export function saveRegisterRetryAt(epochMs: number): void {
  try { localStorage.setItem(KEY_REGISTER_RETRY_AT, String(epochMs)) } catch { /* ignore */ }
}

export function clearRegisterRetryAt(): void {
  try { localStorage.removeItem(KEY_REGISTER_RETRY_AT) } catch { /* ignore */ }
}

export function saveCredentials(token: string, screenId: number): void {
  localStorage.setItem(KEY_TOKEN, token)
  localStorage.setItem(KEY_SCREEN_ID, String(screenId))
}

/** Persist the latest ContentResponse so the next cold-boot can render instantly. */
export function saveCachedContent(content: ContentResponse): void {
  try {
    localStorage.setItem(KEY_CACHED, JSON.stringify(content))
    // Stamp the fetch time alongside the content so the cache TTL survives
    // page reloads — without this a reload after a long offline gap would
    // reset the timer and the cache could outlive the operator's TTL.
    localStorage.setItem(KEY_LAST_FETCHED, String(Date.now()))
  } catch {
    // Quota errors (typically QuotaExceededError) — drop the cache and move on
    // rather than crash the player; the next refresh will retry.
    try {
      localStorage.removeItem(KEY_CACHED)
      localStorage.removeItem(KEY_LAST_FETCHED)
    } catch { /* ignore */ }
  }
}

export function loadCachedContent(): ContentResponse | null {
  const raw = localStorage.getItem(KEY_CACHED)
  if (!raw) return null
  try {
    return JSON.parse(raw) as ContentResponse
  } catch {
    return null
  }
}

/** Epoch-ms of the last successful /content fetch, or null if never fetched. */
export function loadLastFetchedAt(): number | null {
  const raw = localStorage.getItem(KEY_LAST_FETCHED)
  if (!raw) return null
  const n = parseInt(raw, 10)
  return Number.isFinite(n) ? n : null
}

/** Force-drop the cached content (e.g. admin hit Evict, or TTL expired).
 *  Keeps creds + uid so the player keeps polling — only the thing being
 *  painted goes away. */
export function clearCachedContent(): void {
  try {
    localStorage.removeItem(KEY_CACHED)
    localStorage.removeItem(KEY_LAST_FETCHED)
  } catch { /* ignore */ }
}

/** Called on 404 (screen unpaired server-side) or offline countdown=0.
 *  Keeps device_uid; clears the rest so the next register matches the
 *  existing Screen row by uid (and gets a fresh pairing code). */
export function clearCredentials(): void {
  localStorage.removeItem(KEY_TOKEN)
  localStorage.removeItem(KEY_SCREEN_ID)
  localStorage.removeItem(KEY_CACHED)
  localStorage.removeItem(KEY_LAST_FETCHED)
  localStorage.removeItem(KEY_DISCONNECT_AT)
  localStorage.removeItem(KEY_COUNTDOWN_HOURS)
  localStorage.removeItem(KEY_COUNTDOWN_LAST_WALL)
  localStorage.removeItem(KEY_REGISTER_RETRY_AT)
}

// ── License countdown persistence ────────────────────────────────────────
// Wire-side fields land here on every successful heartbeat / content /
// license_renewed WS event. saveLicenseState consolidates the writes so
// callers don't have to remember the key set.

export interface LicenseSnapshot {
  /** ISO-8601 UTC of the license expiry. Display only. */
  disconnectAt: string | null
  /** Server-computed hours of license remaining. null = perpetual. */
  hoursUntilDisconnect: number | null
}

export function saveLicenseState(snap: LicenseSnapshot): void {
  try {
    if (snap.disconnectAt) localStorage.setItem(KEY_DISCONNECT_AT, snap.disconnectAt)
    else                   localStorage.removeItem(KEY_DISCONNECT_AT)
    if (snap.hoursUntilDisconnect !== null && Number.isFinite(snap.hoursUntilDisconnect)) {
      localStorage.setItem(KEY_COUNTDOWN_HOURS, String(snap.hoursUntilDisconnect))
      localStorage.setItem(KEY_COUNTDOWN_LAST_WALL, String(Date.now()))
    } else {
      // Perpetual license — clear any stale countdown so the loop doesn't
      // race a no-longer-applicable expiry.
      localStorage.removeItem(KEY_COUNTDOWN_HOURS)
      localStorage.removeItem(KEY_COUNTDOWN_LAST_WALL)
    }
  } catch { /* quota / SecurityError — best effort */ }
}

export function loadCountdownHours(): number | null {
  const raw = localStorage.getItem(KEY_COUNTDOWN_HOURS)
  if (!raw) return null
  const n = parseFloat(raw)
  return Number.isFinite(n) ? n : null
}

export function saveCountdownHours(hours: number): void {
  try { localStorage.setItem(KEY_COUNTDOWN_HOURS, String(hours)) } catch { /* ignore */ }
}

export function loadCountdownLastWall(): number | null {
  const raw = localStorage.getItem(KEY_COUNTDOWN_LAST_WALL)
  if (!raw) return null
  const n = parseInt(raw, 10)
  return Number.isFinite(n) ? n : null
}

export function saveCountdownLastWall(wallMs: number): void {
  try { localStorage.setItem(KEY_COUNTDOWN_LAST_WALL, String(wallMs)) } catch { /* ignore */ }
}

export function loadDisconnectAt(): string | null {
  return localStorage.getItem(KEY_DISCONNECT_AT)
}
